import { ChevronRight, Home } from 'lucide-react';
import { Link, useMatches, useParams } from 'react-router-dom';
import { ROUTER_PATHS } from '@/app/routes';
import { useTenantId } from '@/features/auth/model/use-tenant-id';
import { usePoCommandCenter } from '@/entities/purchase-order/model';
import { usePoLineSummary } from '@/features/po-line-detail/model/hooks';

export interface CrumbItem {
	label: string;
	path?: string;
}

export interface CrumbParams extends Record<string, string | undefined> {
	poLabel?: string;
	lineLabel?: string;
}

export interface RouteHandle {
	crumb?: (params: CrumbParams) => CrumbItem[];
}

export function PageBreadcrumb() {
	const matches = useMatches();
	const { poId, lineId } = useParams<{ poId?: string; lineId?: string }>();
	const identifier = useTenantId();

	// Hits React Query cache — no new network requests if the page already fetched this data
	const { data: poData } = usePoCommandCenter(identifier, poId);
	const { data: lineData } = usePoLineSummary(identifier, lineId);

	const crumbs = matches
		.filter((match) => (match.handle as RouteHandle)?.crumb)
		.flatMap((match) => {
			const handle = match.handle as RouteHandle;
			return handle.crumb!({
				...(match.params as Record<string, string | undefined>),
				poLabel: poId ? (poData?.ifsPoNumber ?? '…') : undefined,
				lineLabel: lineId ? `Line ${lineData?.lineNumber ?? '…'}` : undefined,
			});
		});

	if (crumbs.length === 0) return null;

	return (
		<nav className="flex items-center text-sm text-gray-500 mb-6">
			<Home className="h-4 w-4 mr-1 text-brand-navy" />
			<Link to={ROUTER_PATHS.root.getLink()} className="text-brand-navy hover:underline transition-colors">
				Dashboard
			</Link>
			{crumbs.map((crumb, index) => (
				<span key={index} className="flex items-center">
					<ChevronRight className="h-4 w-4 mx-1 text-gray-400" />
					{crumb.path && index < crumbs.length - 1 ? (
						<Link to={crumb.path} className="text-brand-navy hover:underline transition-colors">
							{crumb.label}
						</Link>
					) : (
						<span className="text-gray-600">{crumb.label}</span>
					)}
				</span>
			))}
		</nav>
	);
}
